import type { UniversityProgram } from './universityData'

// Colorado Christian University online catalog.
//
// `programId` is CCU's own mProgram id — it identifies the program to the
// advertiser, so it must never be renumbered to match this file's order.
// `category` values are the area-of-study labels the cascade dropdown groups
// by, and must match CCU_AREAS exactly.
//
// Names are the abbreviated catalog titles (B.S., M.Ed., …) rather than the
// spelled-out degree names: this is a 70+ item dropdown, and short labels stay
// readable in a narrow select on mobile.

export const CCU_AREAS = [
  'Business',
  'Criminal Justice',
  'Education',
  'Health Care',
  'Information Technology',
  'Liberal Arts',
  'Psychology & Counseling',
  'Religious Studies & Ministry',
] as const

const ASSOCIATE = 'Associate'
const BACHELORS = "Bachelor's"
const MASTERS = "Master's"

const BUSINESS = 'Business'
const CRIMINAL_JUSTICE = 'Criminal Justice'
const EDUCATION = 'Education'
const HEALTH_CARE = 'Health Care'
const IT = 'Information Technology'
const LIBERAL_ARTS = 'Liberal Arts'
const PSYCHOLOGY = 'Psychology & Counseling'
const RELIGIOUS_STUDIES = 'Religious Studies & Ministry'

export const CCU_PROGRAMS: UniversityProgram[] = [
  // ---- Associate -------------------------------------------------------
  {
    name: 'A.S. Project Management',
    category: BUSINESS,
    degreeLevel: ASSOCIATE,
    programId: 1531,
  },

  // ---- Bachelor's · Business -------------------------------------------
  {
    name: 'B.S. Accountancy',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 620,
  },
  {
    name: 'B.S. Business Administration',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 634,
  },
  {
    name: 'B.S. Business – Entrepreneurship',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 635,
  },
  {
    name: 'B.S. Business – International Business',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 636,
  },
  {
    name: 'B.S. Economics',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 671,
  },
  {
    name: 'B.S. Human Resource Management',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 682,
  },
  {
    name: 'B.S. Logistics and Supply Chain Management',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 688,
  },
  {
    name: 'B.S. Marketing and Sales',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 690,
  },
  {
    name: 'B.S. Nonprofit Management',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 692,
  },
  {
    name: 'B.S. Organizational Management in Project Management',
    category: BUSINESS,
    degreeLevel: BACHELORS,
    programId: 699,
  },

  // ---- Bachelor's · Criminal Justice -----------------------------------
  {
    name: 'B.S. Criminal Justice',
    category: CRIMINAL_JUSTICE,
    degreeLevel: BACHELORS,
    programId: 659,
  },

  // ---- Bachelor's · Education ------------------------------------------
  {
    name: 'B.A. Early Childhood Education (Licensure)',
    category: EDUCATION,
    degreeLevel: BACHELORS,
    programId: 669,
  },
  {
    name: 'B.A. Early Childhood Education (without Licensure)',
    category: EDUCATION,
    degreeLevel: BACHELORS,
    programId: 670,
  },
  {
    name: 'B.A. Elementary Education (Licensure)',
    category: EDUCATION,
    degreeLevel: BACHELORS,
    programId: 676,
  },
  {
    name: 'B.A. Elementary Education (without Licensure)',
    category: EDUCATION,
    degreeLevel: BACHELORS,
    programId: 677,
  },
  {
    name: 'B.A. Special Education, K-12 Generalist (Licensure)',
    category: EDUCATION,
    degreeLevel: BACHELORS,
    programId: 710,
  },
  {
    name: 'B.A. Special Education, K-12 Generalist (without Licensure)',
    category: EDUCATION,
    degreeLevel: BACHELORS,
    programId: 711,
  },

  // ---- Bachelor's · Health Care ----------------------------------------
  {
    name: 'B.S. Health Care Administration',
    category: HEALTH_CARE,
    degreeLevel: BACHELORS,
    programId: 680,
  },

  // ---- Bachelor's · Information Technology -----------------------------
  {
    name: 'B.S. Computer Information Technology',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 651,
  },
  {
    name: 'B.S. CIT – Cyber Security',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 652,
  },
  {
    name: 'B.S. CIT – Database Management',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 653,
  },
  {
    name: 'B.S. CIT – Data Engineering',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 719,
  },
  {
    name: 'B.S. CIT – Networking',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 654,
  },
  {
    name: 'B.S. Computer Science',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 656,
  },
  {
    name: 'B.S. Cyber Security',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 1767,
  },
  {
    name: 'B.S. Data Analytics',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 668,
  },
  {
    name: 'B.S. Information Systems Management',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 684,
  },
  {
    name: 'B.S. Information Systems Management – Cyber Security',
    category: IT,
    degreeLevel: BACHELORS,
    programId: 686,
  },

  // ---- Bachelor's · Liberal Arts ---------------------------------------
  {
    name: 'B.A. Communication Studies',
    category: LIBERAL_ARTS,
    degreeLevel: BACHELORS,
    programId: 649,
  },
  {
    name: 'B.A. Creative Writing',
    category: LIBERAL_ARTS,
    degreeLevel: BACHELORS,
    programId: 657,
  },

  // ---- Bachelor's · Psychology & Counseling ----------------------------
  {
    name: 'B.S. Applied Psychology',
    category: PSYCHOLOGY,
    degreeLevel: BACHELORS,
    programId: 704,
  },
  {
    name: 'B.S. Applied Psychology – Biblical Studies',
    category: PSYCHOLOGY,
    degreeLevel: BACHELORS,
    programId: 705,
  },
  {
    name: 'B.S. Applied Psychology – Clinical Counseling',
    category: PSYCHOLOGY,
    degreeLevel: BACHELORS,
    programId: 706,
  },
  {
    name: 'B.S. Applied Psychology – Criminal Justice',
    category: PSYCHOLOGY,
    degreeLevel: BACHELORS,
    programId: 707,
  },

  // ---- Bachelor's · Religious Studies & Ministry -----------------------
  {
    name: 'B.A. Biblical Studies',
    category: RELIGIOUS_STUDIES,
    degreeLevel: BACHELORS,
    programId: 631,
  },
  {
    name: 'B.A. Christian Ministry',
    category: RELIGIOUS_STUDIES,
    degreeLevel: BACHELORS,
    programId: 643,
  },

  // ---- Master's · Business ---------------------------------------------
  {
    name: 'M.S. Accounting',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 621,
  },
  {
    name: 'M.B.A. – Advanced Accounting',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 637,
  },
  {
    name: 'M.B.A. – Cyber Security',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 638,
  },
  {
    name: 'M.B.A. – Economics',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 718,
  },
  {
    name: 'M.B.A. – Enterprise Agility',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 642,
  },
  {
    name: 'M.B.A. – Health Care Administration',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 639,
  },
  {
    name: 'M.B.A. – Leadership',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 640,
  },
  {
    name: 'M.B.A. – Project Management',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 641,
  },
  {
    name: 'M.S. Human Resource Management',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 683,
  },
  {
    name: 'M.S. Organizational Leadership',
    category: BUSINESS,
    degreeLevel: MASTERS,
    programId: 701,
  },

  // ---- Master's · Criminal Justice -------------------------------------
  {
    name: 'M.S. Criminal Justice',
    category: CRIMINAL_JUSTICE,
    degreeLevel: MASTERS,
    programId: 660,
  },
  {
    name: 'M.S. Criminal Justice – Campus, Event, and Organizational Safety',
    category: CRIMINAL_JUSTICE,
    degreeLevel: MASTERS,
    programId: 661,
  },

  // ---- Master's · Education --------------------------------------------
  {
    name: 'M.A. Curriculum and Instruction',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 665,
  },
  {
    name: 'M.A. C&I – Alternative Licensing',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 662,
  },
  {
    name: 'M.A. C&I – Biblical Studies',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 663,
  },
  {
    name: 'M.A. C&I – Culturally and Linguistically Diverse',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 664,
  },
  {
    name: 'M.A. School Counseling',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 709,
  },
  {
    name: 'M.Ed. Educational Leadership (Principal Licensure)',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 673,
  },
  {
    name: 'M.Ed. Educational Leadership (without Licensure)',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 674,
  },
  {
    name: 'M.Ed. Education – TESOL',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 675,
  },
  {
    name: 'M.Ed. Special Education – Alternative Licensing',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 713,
  },
  {
    name: 'M.Ed. Special Education – Generalist Endorsement',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 714,
  },
  {
    name: 'M.Ed. Special Education (Licensure)',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 715,
  },
  {
    name: 'M.Ed. Special Education (without Licensure)',
    category: EDUCATION,
    degreeLevel: MASTERS,
    programId: 712,
  },

  // ---- Master's · Information Technology -------------------------------
  {
    name: 'M.S. Cyber Security',
    category: IT,
    degreeLevel: MASTERS,
    programId: 666,
  },

  // ---- Master's · Psychology & Counseling ------------------------------
  {
    name: 'M.A. Clinical Mental Health Counseling – Marriage & Family Therapy',
    category: PSYCHOLOGY,
    degreeLevel: MASTERS,
    programId: 646,
  },
  {
    name: 'M.A. CMHC – Online Curriculum',
    category: PSYCHOLOGY,
    degreeLevel: MASTERS,
    programId: 645,
  },
  {
    name: 'M.A. CMHC – On Site Curriculum',
    category: PSYCHOLOGY,
    degreeLevel: MASTERS,
    programId: 644,
  },
  {
    name: 'M.A. CMHC – Substance Use Disorders',
    category: PSYCHOLOGY,
    degreeLevel: MASTERS,
    programId: 647,
  },
  {
    name: 'M.A. Counseling Dual – Mental Health & School',
    category: PSYCHOLOGY,
    degreeLevel: MASTERS,
    programId: 723,
  },
  {
    name: 'M.S. Psychology',
    category: PSYCHOLOGY,
    degreeLevel: MASTERS,
    programId: 703,
  },

  // ---- Master's · Religious Studies & Ministry -------------------------
  {
    name: 'M.A. Biblical Studies',
    category: RELIGIOUS_STUDIES,
    degreeLevel: MASTERS,
    programId: 632,
  },
  {
    name: 'M.A. Executive Leadership in Christian Ministry',
    category: RELIGIOUS_STUDIES,
    degreeLevel: MASTERS,
    programId: 678,
  },
  {
    name: 'M.A. Theological Studies',
    category: RELIGIOUS_STUDIES,
    degreeLevel: MASTERS,
    programId: 716,
  },
]
